import React, { FC, useEffect, useMemo, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { NavLink } from 'react-router-dom';
import moment from 'moment';

import './payout-list.scss';
import CommonHeader from '../../../../shared/common-header/common-header';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { payoutsActions, fetchMerchantPayouts } from '../../../../reducers/payoutsSlice';
import { Payouts } from '../../../../models/payouts';
import { INTEGOLF_MERCHANT_PAYOUT_STATUS_PENDING, INTEGOLF_MERCHANT_PAYOUT_STATUS_APPROVED, INTEGOLF_MERCHANT_PAYOUT_STATUS_READY } from "../../../../utils/constants";
import { useTranslation } from 'react-i18next';

interface PayoutListProps { }

const PayoutElement: FC<Payouts> = (payoutObject: Payouts) => {
  const { t } = useTranslation("")
  const toNumber = (v: any) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };
  const getRate = () => {
    const rate = Number((payoutObject as any)?.payoutConversion?.exchangeRate ?? 0);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  };
  const toCad = (amount: any) => {
    const n = toNumber(amount);
    const isUsd = String((payoutObject as any)?.payoutCurrency || "").toUpperCase() === "USD";
    return isUsd ? n * getRate() : n;
  };
  const formatCad = (amount: any) =>
    `$ ${toNumber(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return <tr key={payoutObject._id}>
    <td>
      <NavLink to={`/transaction/history/${payoutObject?._id}`} className="text-decoration-none">
        <h6 className="mb-0 fw700" style={{ color: "#00D19F" }}>{payoutObject?.payoutId}</h6>
      </NavLink>
    </td>
    <td>{moment(payoutObject.upcomingPayout).format("dddd, DD MMM YYYY")}</td>
    <td>
      {
        payoutObject?.status == INTEGOLF_MERCHANT_PAYOUT_STATUS_PENDING && <label className="mb-0 badge badgeStatus pending">{t("transactionsList.PendingPayouts")}</label>
      }
      {
        payoutObject?.status == INTEGOLF_MERCHANT_PAYOUT_STATUS_READY && <label className="mb-0 badge badgeStatus">{t("transactionsList.ReadyPayouts")}</label>
      }
      {
        payoutObject?.status == INTEGOLF_MERCHANT_PAYOUT_STATUS_APPROVED && <label className="mb-0 badge badgeStatus success">{t("transactionsList.PaidPayouts")}</label>
      }
    </td>
    <td>{payoutObject?.bookings}</td>
    <td>
      <h6 className="mb-0 fw700">{toNumber(payoutObject?.total_amount) > 0 ? formatCad(toCad(payoutObject?.total_amount)) : '$ 0.00'}</h6>
    </td>
    <td>
      <h6 className="mb-0 fw700">{toNumber(payoutObject?.total_product_amount) > 0 ? formatCad(toCad(payoutObject?.total_product_amount)) : "N/A"}</h6>
    </td>
    <td> {toNumber(payoutObject?.fees) > 0 ? `- ${formatCad(toCad(payoutObject?.fees))}` : '$ 0.00'} </td>
    <td> {toNumber(payoutObject?.product_fees) > 0 ? `- ${formatCad(toCad(payoutObject?.product_fees))}` : "N/A"} </td>
    <td>
      <h6 className="mb-0 fw700">{toNumber(payoutObject?.payout_amount) > 0 ? formatCad(toCad(payoutObject?.payout_amount)) : '$ 0.00'}</h6>
    </td>
    {/* <td>****6453</td> */}
    <td>
      <div className="listActionDropdownBlock">
        <Dropdown align="end">
          <Dropdown.Toggle className="toggleButton">
            <em className="icon-vertical-elipse"></em>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <NavLink
              to={`/transaction/history/${payoutObject?._id}`}
              className="dropdown-item"
            >
              {t("transactionsList.ViewDetail")}
            </NavLink>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </td>
  </tr>;
}

const PayoutList: FC<PayoutListProps> = () => {

  const dispatch = useAppDispatch();
  const merchantPayouts: Array<Payouts> = useAppSelector(fetchMerchantPayouts) || [];
  const [selectedMonth, setSelectedMonth] = useState<string>('ALL');
  const [selectedYear, setSelectedYear] = useState<string>('ALL');

  useEffect(() => {
    dispatch(payoutsActions.payoutsList());
  }, []);

  const { t } = useTranslation("");

  const monthOptions = useMemo(() => ([
    { value: 'ALL', label: 'All months' },
    { value: '1', label: 'Jan' },
    { value: '2', label: 'Feb' },
    { value: '3', label: 'Mar' },
    { value: '4', label: 'Apr' },
    { value: '5', label: 'May' },
    { value: '6', label: 'Jun' },
    { value: '7', label: 'Jul' },
    { value: '8', label: 'Aug' },
    { value: '9', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
  ]), []);

  const toNumber = (v: any) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  const getRate = (payout: any) => {
    const rate = Number(payout?.payoutConversion?.exchangeRate ?? 0);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  };

  const toCadByPayout = (payout: any, amount: any) => {
    const n = toNumber(amount);
    const isUsd = String(payout?.payoutCurrency || "").toUpperCase() === "USD";
    return isUsd ? n * getRate(payout) : n;
  };

  const formatCad = (amount: any) =>
    `$ ${toNumber(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const availableYears = useMemo(() => {
    const yearSet = new Set<number>();
    for (const p of merchantPayouts) {
      const millis = Number(p?.upcomingPayout ?? p?.createdAt ?? 0);
      if (!millis) continue;
      const year = Number(moment(millis).format("YYYY"));
      if (Number.isFinite(year)) yearSet.add(year);
    }
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [merchantPayouts]);

  const matchesMonthYear = (p: any) => {
    const millis = Number(p?.upcomingPayout ?? p?.createdAt ?? 0);
    if (!millis) return false;
    const m = Number(moment(millis).format("M"));
    const y = Number(moment(millis).format("YYYY"));
    if (selectedMonth !== 'ALL' && m !== Number(selectedMonth)) return false;
    if (selectedYear !== 'ALL' && y !== Number(selectedYear)) return false;
    return true;
  };

  const filteredPayouts = useMemo(() => {
    if (selectedMonth === 'ALL' && selectedYear === 'ALL') return merchantPayouts;
    return merchantPayouts.filter(matchesMonthYear);
  }, [merchantPayouts, selectedMonth, selectedYear]);

  const completedPayouts = useMemo(
    () => filteredPayouts.filter((p) => p?.status === INTEGOLF_MERCHANT_PAYOUT_STATUS_APPROVED),
    [filteredPayouts]
  );
  const readyPayouts = useMemo(
    () => filteredPayouts.filter((p) => p?.status === INTEGOLF_MERCHANT_PAYOUT_STATUS_READY),
    [filteredPayouts]
  );
  const pendingPayouts = useMemo(
    () => filteredPayouts.filter((p) => p?.status === INTEGOLF_MERCHANT_PAYOUT_STATUS_PENDING),
    [filteredPayouts]
  );

  const completedAmount = completedPayouts.reduce((sum, p) => sum + toCadByPayout(p, p?.payout_amount), 0);
  const previousPayout = [...completedPayouts].sort(
    (a, b) => Number(b?.upcomingPayout ?? b?.createdAt ?? 0) - Number(a?.upcomingPayout ?? a?.createdAt ?? 0)
  )[0];
  const pendingAmount = [...readyPayouts, ...pendingPayouts].reduce((sum, p) => sum + toCadByPayout(p, p?.payout_amount), 0);
  const pendingBadgeLabel = readyPayouts.length > 0 && pendingPayouts.length > 0
    ? "Ready + Pending"
    : readyPayouts.length > 0
      ? "Ready"
      : "Pending";

  return (
    <>
      <div className="transition-history">
        <CommonHeader sectionTitle={t("transactionsList.title")} hasExportButton={false}></CommonHeader>
        <div className="commonHeader transitionWrapper__header pt-0 pb-4">
          <div className="container">
            <div className="row gutters-10 align-items-center">
              <div className="col">
                <div className="row gutters-10">
                  <div className="col-auto">
                    <div className="_tBlock mb-2">
                      <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <label className="mr-2 mb-2">Total amount received</label>
                      </div>
                      <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <h5 className="mb-0"> {completedAmount > 0 ? formatCad(completedAmount) : "$ 0.00"}</h5>
                        <label className="badge mb-0">Completed</label>
                      </div>
                    </div>
                  </div>
                  {/* <div className="col-auto">
                    <div className="_tBlock mb-2">
                      <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <label className="mr-2 mb-2">{t("transactionsList.PreviousPayout")}</label>
                        <label className="_date mb-2">{previousPayout?.upcomingPayout ? moment(previousPayout.upcomingPayout).format("MMM DD, YYYY") : "-"}</label>
                      </div>
                      <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <h5 className="mr-2 mb-0">{toNumber(previousPayout?.payout_amount) > 0 ? formatCad(toCadByPayout(previousPayout, previousPayout?.payout_amount)) : "$ 0.00"}</h5>
                        <label className="badge mb-0">{t("transactionsList.Paid")}</label>
                      </div>
                    </div>
                  </div> */}
                  <div className="col-auto">
                    <div className="_tBlock mb-2">
                      <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <label className="mr-2 mb-2">Pending</label>
                      </div>
                      <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <h5 className="mr-2 mb-0">{pendingAmount > 0 ? formatCad(pendingAmount) : "$ 0.00"}</h5>
                        {pendingBadgeLabel === "Ready + Pending" ? (
                          <label className="badge mb-0">{pendingBadgeLabel}</label>
                        ) : pendingBadgeLabel === "Ready" ? (
                          <label className="badge mb-0">Ready</label>
                        ) : (
                          <label className="badge pending mb-0">Pending</label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="col-auto">
            <button
              type="button"
              className="_actionButton dropdown-toggle btn btn-primary mb-2"
            >
              <em className="icon-export me-2"></em> Export
            </button>
          </div> */}
            </div>
          </div>
        </div>
        <div className="transitionWrapper__body">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="d-flex align-items-end justify-content-between flex-wrap mb-3">
                  <h5 className="mb-2">{t("transactionsList.title")}</h5>
                  <div className="d-flex align-items-end gap-2 flex-wrap mb-2">
                    <div>
                      <label className="mb-2">Month</label>
                      <select
                        className="form-select"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      >
                        {monthOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-2">Year</label>
                      <select
                        className="form-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                      >
                        <option value="ALL">All years</option>
                        {availableYears.map((year) => (
                          <option key={year} value={String(year)}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-border">
                    <thead>
                      <tr>
                        <th>{t("transactionsList.tableHeadings.PayoutID")}</th>
                        <th>{t("transactionsList.tableHeadings.Date")}
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        <th>{t("transactionsList.tableHeadings.Status")}
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        <th>{t("transactionsList.tableHeadings.Bookings")}
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        <th>Golf Price (CAD)
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        <th>Total Products Price (CAD)
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        <th>Integolf Fees (CAD)
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        <th>Product Fees (CAD)
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        <th>Golf Payout Amount (CAD)
                          {/* <em className="fa-solid fa-sort ms-1 color-700"></em> */}
                        </th>
                        {/* <th>Transferred in</th> */}
                        <th className="w40">&nbsp;</th>
                      </tr>
                    </thead>
                    {
                      filteredPayouts?.length ?
                        <tbody>
                          {filteredPayouts.map((payout: Payouts) => <PayoutElement {...payout} />)}
                        </tbody>
                        :
                        <div className='bookingsListWrapper' id='payoutListWrapper'>
                          <div className="noBookingBlock text-center mt-4 py-5">
                            <img
                              src={`${process.env.PUBLIC_URL}/assets/images/no-booking.png`}
                              alt="no-booking"
                              className="img-fluid mx-auto d-block"
                            />
                            <h5 className="mt-4 pt-3 fontsize-16 fw600">
                              {t("transactionsList.NoPayoutMessage")}
                            </h5>
                          </div>
                        </div>
                    }
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div></>
  )
};

export default PayoutList;
